import React from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { ButtonTs, FilterLabel } from '@lt/components';
import { useFreezeBody } from '@lt/components/hooks';
import { useTranslation, i18n } from '@lt/localization';

import { getIsMobile } from 'src/store/view/selectors';
import { toggleFiltersPanelAction } from 'checkout/store/actions/view';
import {
  flightsCountSelector,
  getFlightsCheapestPrice,
  loadingStateSelector,
} from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/selectors';
import { isArray } from 'lodash';
import FILTER_LIST from './filters';

import {
  changeFlightsFilter,
  changeFlightsOneSliderFilter,
  changeFlightsSliderFilter,
  clearAllFligthsFilters,
  clearFlightsFilter,
  getFlightsFilterData,
  getHasActiveFilters,
  minPriceForFilterByTourIdSelector,
  sliderFilters,
} from '../../store/flights-filters';

import { customCheckboxStylesForMobile } from './styles';
import type { FilterPanelPropsMobile } from './types';
import { getButtonText } from './utils';
import { applySingleChoiseFilters } from '../../store/flights-filters/actions';

const portalTarget = document.querySelector('.lt-react-app-root');

const FiltersPanelMobile = ({
  getFilterData,
  isMobile,
  changeFilter,
  clearFilter,
  changeSliderFilter,
  changeOneSliderFilter,
  hasActiveFilters,
  toggleFiltersPanel,
  clearAllFilters,
  getMinPriceForFilter,
  flightsCount,
  loadingState,
  changeSingleOption,
  minPriceForSliderFilters,
}: FilterPanelPropsMobile): JSX.Element | null => {
  const { setIsFreeze } = useFreezeBody(true);
  const { t } = useTranslation();

  const handleCloseFiltersPanel = () => {
    setIsFreeze(false);
    toggleFiltersPanel();
  };

  const handleClearFilters = () => clearAllFilters();

  if (!portalTarget) return null;

  return createPortal(
    <StyledFiltersPanel>
      <StyledButtonSection>
        <StyledButton onClick={handleCloseFiltersPanel}>
          {t('filters.panel.cancel')}
        </StyledButton>
        {hasActiveFilters && (
          <StyledButton onClick={handleClearFilters}>
            {t('checkout.filter.clear')}
          </StyledButton>
        )}
      </StyledButtonSection>
      {FILTER_LIST.map((Filter) => {
        const { filterName, getFilterVisibleName, getFilterDescription } =
          Filter;

        // adding names and descriptions
        const filterData = getFilterData(filterName);

        if (
          !filterData ||
          (!sliderFilters.includes(filterName) && !isArray(filterData)) ||
          (isArray(filterData) && !filterData?.length)
        )
          return null;

        if (sliderFilters.includes(filterName))
          return (
            <Filter
              data={filterData}
              onClear={clearFilter}
              isMobile={isMobile}
              key={filterName}
              onChange={
                filterName === 'max_flight_duration'
                  ? changeOneSliderFilter
                  : changeSliderFilter
              }
              minPrice={minPriceForSliderFilters}
              Filter={Filter}
            />
          );

        if (!isArray(filterData)) return null;

        // adding names and descriptions
        const data = filterData.map((filterOptionData) => ({
          ...filterOptionData,
          name: getFilterVisibleName(filterOptionData),
          min_price: getMinPriceForFilter(filterOptionData),
          description:
            getFilterDescription && getFilterDescription(filterOptionData),
        }));

        return (
          <Filter
            data={data}
            isMobile={isMobile}
            onChange={changeFilter}
            onChangeSingleOption={changeSingleOption}
            onClear={clearFilter}
            Filter={Filter}
            key={filterName}
            LabelComponent={StyledFilterLabel}
            // custom styles for mobile
            customCheckboxStyles={customCheckboxStylesForMobile}
          />
        );
      })}
      <ApplyButtonContainer>
        <ApplyButton
          disabled={flightsCount === 0}
          onClick={handleCloseFiltersPanel}
        >
          {getButtonText(flightsCount, loadingState)}
        </ApplyButton>
      </ApplyButtonContainer>
    </StyledFiltersPanel>,
    portalTarget,
  );
};

const ApplyButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  text-align: center;
  padding: 16px 0 16px;
  font-size: 20px;
`;

const ApplyButton = styled(ButtonTs).attrs({ variant: 'primary' })`
  display: block;
  margin: 0 20px;
  height: 46px;
  width: calc(100% - 40px);
`;

const StyledButtonSection = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  padding: 13px 10px;
`;

const StyledButton = styled.span`
  color: var(--secondary);
  font-size: 16px;
  background: none;
  padding: 0;
  border: 0;
`;

const StyledFilterLabel = styled(FilterLabel).attrs({
  emptyMessage: i18n.t('checkout.filter.empty'),
  subjectToPlural: 'FLIGHTS',
})``;

const StyledFiltersPanel = styled.div`
  inset: 0;
  position: fixed;
  z-index: 1000;
  background-color: var(--white);
  overflow-y: auto;
  overflow-x: hidden;
`;

const mapPropsToState = (state) => ({
  isMobile: getIsMobile(state),
  hasActiveFilters: getHasActiveFilters(state),
  getFilterData: (filterName) => getFlightsFilterData(state, filterName),
  getMinPriceForFilter: (filterOptionData) =>
    minPriceForFilterByTourIdSelector(state, filterOptionData),
  flightsCount: flightsCountSelector(state),
  loadingState: loadingStateSelector(state),
  minPriceForSliderFilters: getFlightsCheapestPrice(state),
});

const mapDispatchToProps = {
  changeFilter: changeFlightsFilter,
  changeSliderFilter: changeFlightsSliderFilter,
  changeOneSliderFilter: changeFlightsOneSliderFilter,
  clearFilter: clearFlightsFilter,
  clearAllFilters: clearAllFligthsFilters,
  toggleFiltersPanel: toggleFiltersPanelAction,
  changeSingleOption: applySingleChoiseFilters,
};

export default connect(mapPropsToState, mapDispatchToProps)(FiltersPanelMobile);
